import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AutoSaveProvider } from './contexts/AutoSaveContext';
import '@/App.css';

// Pages
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import Dashboard from './pages/Dashboard';
import ResourcesPageNew from './pages/ResourcesPageNew';
import ResourceProfilePage from './pages/ResourceProfilePage';
import ProjectsPageNew from './pages/ProjectsPageNew';
import ProjectDetailPageUser from './pages/ProjectDetailPage';
import PublicProjectPage from './pages/PublicProjectPage';
import MatchingPage from './pages/MatchingPage';
import MessagesPage from './pages/MessagesPage';
import PricingPage from './pages/PricingPage';
import ProfilePage from './pages/ProfilePage';
import GettingStartedPage from './pages/GettingStartedPage';
import AnalyticsPage from './pages/AnalyticsPage';
import CompanyProfilePage from './pages/CompanyProfilePage';
import ReservationsPage from './pages/ReservationsPage';
import ProposalsPage from './pages/ProposalsPage';
import SupportPage from './pages/SupportPage';
import UserTicketDetailPage from './pages/UserTicketDetailPage';
import FavoritesPage from './pages/FavoritesPage';
import CollaborationsPage from './pages/CollaborationsPage';
import CreateContractPage from './pages/CreateContractPage';
import ContractTemplatesPage from './pages/ContractTemplatesPage';
import MyContractTemplatesPage from './pages/MyContractTemplatesPage';
import ContractTemplateViewPage from './pages/ContractTemplateViewPage';
import FreelanceChooseModelPage from './pages/FreelanceChooseModelPage';
import InterviewsPage from './pages/InterviewsPage';

// Layouts
import RMPMLayout from './components/RMPMLayout';

// Components
import CookieConsent from './components/CookieConsent';

// Admin Pages
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import UsersPage from './pages/admin/UsersPage';
import UserDetailPage from './pages/admin/UserDetailPage';
import CompaniesPage from './pages/admin/CompaniesPage';
import CompanyDetailPage from './pages/admin/CompanyDetailPage';
import ResourcesAdminPage from './pages/admin/ResourcesAdminPage';
import ResourceDetailPage from './pages/admin/ResourceDetailPage';
import ProjectsAdminPage from './pages/admin/ProjectsAdminPage';
import ProjectDetailPage from './pages/admin/ProjectDetailPage';
import ReservationsAdminPage from './pages/admin/ReservationsAdminPage';
import ReservationDetailPage from './pages/admin/ReservationDetailPage';
import MessagingAdminPage from './pages/admin/MessagingAdminPage';
import ConversationDetailPage from './pages/admin/ConversationDetailPage';
import SupportTicketsAdminPage from './pages/admin/SupportTicketsAdminPage';
import TicketDetailPage from './pages/admin/TicketDetailPage';
import AuditLogsPage from './pages/admin/AuditLogsPage';
import SettingsPage from './pages/admin/SettingsPage';
import SecurityPage from './pages/SecurityPage';
import ConfidentialityPage from './pages/ConfidentialityPage';
import ExportsPage from './pages/admin/ExportsPage';
import PasswordResetsPage from './pages/admin/PasswordResetsPage';
import DataExportPage from './pages/admin/DataExportPage';
import CollaborationsAdminPage from './pages/admin/CollaborationsAdminPage';
import ActionItemsPage from './pages/admin/ActionItemsPage';
import CommercialActivityPage from './pages/admin/CommercialActivityPage';
import ContractUsagePage from './pages/admin/ContractUsagePage';
import NotificationPrefsPage from './pages/admin/NotificationPrefsPage';
import UserActivityPage from './pages/admin/UserActivityPage';
import ActivityReportsPage from './pages/admin/ActivityReportsPage';
import AnalyticsDashboard from './pages/admin/AnalyticsDashboard';
import PilotDashboardPage from './pages/admin/PilotDashboardPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AIDisclaimerPage from './pages/AIDisclaimerPage';
import VisionPage from './pages/VisionPage';
import LegalNoticePage from './pages/LegalNoticePage';
import CareersPage from './pages/CareersPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Auth Context
export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Merge subscription info with user data
        const userData = {
          ...response.data.user,
          subscription_tier: response.data.subscription_tier,
          subscription_status: response.data.subscription_status,
          pilot_status: response.data.pilot_status,
          pilot_unlimited: response.data.pilot_unlimited,
          pilot_expires_at: response.data.pilot_expires_at
        };
        setUser(userData);
        setCompany(response.data.company);
      } catch (error) {
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
    checkAuth();
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setCompany(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID} nonce="" locale="en">
      <AuthContext.Provider value={{ user, company, login, logout, checkAuth }}>
        <AutoSaveProvider>
          <BrowserRouter>
          <Routes>
          {/* Public Routes */}
          <Route 
            path="/" 
            element={
              user 
                ? user.role === 'super_admin' 
                  ? <Navigate to="/admin" /> 
                  : <Navigate to="/dashboard" />
                : <LandingPage />
            } 
          />
          <Route 
            path="/auth" 
            element={
              user 
                ? user.role === 'super_admin' 
                  ? <Navigate to="/admin" /> 
                  : <Navigate to="/dashboard" />
                : <AuthPage />
            } 
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
          <Route path="/public/projects/:projectId" element={<PublicProjectPage />} />
          {/* Pricing page - accessible publicly and by authenticated users */}
          <Route path="/pricing" element={<PricingPage />} />
          {/* Legal pages - publicly accessible */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/terms-of-service" element={<TermsPage />} />
          <Route path="/ai-disclaimer" element={<AIDisclaimerPage />} />
          <Route path="/vision" element={<VisionPage />} />
          <Route path="/legal-notice" element={<LegalNoticePage />} />
          <Route path="/imprint" element={<LegalNoticePage />} />
          <Route path="/careers" element={<CareersPage />} />
          
          {/* Company Profile - Accessible by all authenticated users including admins */}
          {user && (
            <Route path="/company/:companyId" element={<CompanyProfilePage />} />
          )}
          
          {/* RM/PM Portal Routes - Using New Layout */}
          <Route
            element={user && user.role !== 'super_admin' ? <RMPMLayout /> : <Navigate to="/auth" />}
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/resources" element={<ResourcesPageNew />} />
            <Route path="/resources/:resourceId" element={<ResourceProfilePage />} />
            <Route path="/projects" element={<ProjectsPageNew />} />
            <Route path="/projects/:projectId" element={<ProjectDetailPageUser />} />
            <Route path="/matching" element={<MatchingPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/interviews" element={<InterviewsPage />} />
            <Route path="/favorites" element={<FavoritesPage />} />
            <Route path="/reservations" element={<ReservationsPage />} />
            <Route path="/proposals" element={<ProposalsPage />} />
            <Route path="/collaborations" element={<CollaborationsPage />} />
            <Route path="/collaborations/:collaborationId/create-contract" element={<CreateContractPage />} />
            <Route path="/contract-templates" element={<ContractTemplatesPage />} />
            <Route path="/my-contract-templates" element={<MyContractTemplatesPage />} />
            <Route path="/contract-templates/:templateId/view" element={<ContractTemplateViewPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/getting-started" element={<GettingStartedPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/support/tickets/:ticketId" element={<UserTicketDetailPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/confidentiality" element={<ConfidentialityPage />} />
            <Route path="/freelance/choose-model" element={<FreelanceChooseModelPage />} />
            <Route path="/freelance/subscription-success" element={<FreelanceChooseModelPage />} />
          </Route>
          
          {/* Admin Routes */}
          <Route
            path="/admin"
            element={user?.role === 'super_admin' ? <AdminLayout /> : <Navigate to="/dashboard" />}
          >
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="users/:userId" element={<UserDetailPage />} />
            <Route path="companies" element={<CompaniesPage />} />
            <Route path="companies/:companyId" element={<CompanyDetailPage />} />
            <Route path="resources" element={<ResourcesAdminPage />} />
            <Route path="resources/:resourceId" element={<ResourceDetailPage />} />
            <Route path="projects" element={<ProjectsAdminPage />} />
            <Route path="projects/:projectId" element={<ProjectDetailPage />} />
            <Route path="reservations" element={<ReservationsAdminPage />} />
            <Route path="reservations/:reservationId" element={<ReservationDetailPage />} />
            <Route path="collaborations" element={<CollaborationsAdminPage />} />
            <Route path="messages" element={<MessagingAdminPage />} />
            <Route path="messages/:threadId" element={<ConversationDetailPage />} />
            <Route path="tickets" element={<SupportTicketsAdminPage />} />
            <Route path="tickets/:ticketId" element={<TicketDetailPage />} />
            <Route path="action-items" element={<ActionItemsPage />} />
            <Route path="commercial" element={<CommercialActivityPage />} />
            <Route path="contract-usage" element={<ContractUsagePage />} />
            <Route path="notification-prefs" element={<NotificationPrefsPage />} />
            <Route path="user-activity" element={<UserActivityPage />} />
            <Route path="activity-reports" element={<ActivityReportsPage />} />
            <Route path="analytics" element={<AnalyticsDashboard />} />
            <Route path="pilot" element={<PilotDashboardPage />} />
            <Route path="exports" element={<ExportsPage />} />
            <Route path="data-export" element={<DataExportPage />} />
            <Route path="audit-logs" element={<AuditLogsPage />} />
            <Route path="password-resets" element={<PasswordResetsPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
        </Routes>
        </BrowserRouter>
      </AutoSaveProvider>
      
      {/* GDPR Cookie Consent Banner */}
      <CookieConsent />
    </AuthContext.Provider>
    </GoogleOAuthProvider>
  );
}

export default App;
